/* Shared cart lifecycle. Loaded before theme/app scripts so their fetch/XHR
 * wrappers still pass through this observer. Payloads are preserved and never
 * replayed. Same-origin cart mutations receive a bounded network timeout. */
(() => {
  'use strict';
  if (window.WWCartSafety) return;

  const originalFetch = window.fetch;
  const pending = new Set();
  const storageKey = 'ww-cart-unconfirmed';
  const checkoutSelector = '[name="checkout"], .cart__checkout-button, a[href*="/checkout"], #CartDrawerCouponForm button[type="submit"]';
  const expressSelector = 'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons, .shopify-payment-button';
  let revision = 0;
  let dirty = false;
  let checking = false;
  let rendering = false;
  let error = '';
  let verified = false;
  let openRequested = false;
  let refreshTimer;
  let uiQueued = false;
  let lastCart = null;
  let errorRevision = -1;
  let adding = false;
  let checkoutHoldUntil = 0;
  let checkoutHoldTimer;
  const locks = new Map();
  const expressLocks = new Map();
  const editLocks = new Map();
  const additions = new Map();

  const busy = () => pending.size > 0 || dirty || checking;
  const blocked = () => busy() || !!error || Date.now() < checkoutHoldUntil;
  const root = () => window.Shopify?.routes?.root || '/';
  function persist() {
    try {
      if (busy() || error) sessionStorage.setItem(storageKey, '1');
      else sessionStorage.removeItem(storageKey);
    } catch (_) { /* Storage is optional; in-page protection still applies. */ }
  }

  function message() {
    if (pending.size) return [...pending].some(op => op.slow)
      ? 'Your cart is taking longer to update. Please wait before checking out.'
      : [...pending].some(op => op.kind === 'add') ? 'Adding to your cart…' : 'Updating your cart…';
    if (checking || dirty) return 'Checking your cart…';
    return error;
  }

  function statusPanel(parent) {
    let panel = parent.querySelector(':scope > [data-cart-safety-status]');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute('data-cart-safety-status', '');
      panel.className = 'cart-safety-status';
      panel.hidden = true;
      panel.innerHTML = '<p role="status" aria-live="polite" aria-atomic="true"></p>' +
        '<div class="cart-safety-actions"><button type="button" data-cart-safety-check>Check cart again</button>' +
        '<button type="button" data-cart-safety-accept>Continue with these items</button></div>';
      parent.prepend(panel);
    }
    const text = panel.querySelector('p');
    if (text.textContent !== message()) text.textContent = message();
    const wasHidden = panel.hidden;
    panel.hidden = !message() || (!busy() && lastCart?.item_count === 0);
    if (wasHidden && !panel.hidden) {
      parent.querySelectorAll('.cart__contents').forEach(el => { el.scrollTop = 0; });
      parent.scrollTop = 0;
    }
    panel.querySelector('.cart-safety-actions').hidden = busy() || !error;
    panel.querySelector('[data-cart-safety-accept]').hidden = !verified || !lastCart?.item_count;
  }

  function updateUI() {
    uiQueued = false;
    if (openRequested && !busy() && verified && !document.querySelector('quick-add-modal[open]')) {
      openRequested = false;
      openDrawer();
    }
    const locked = blocked();
    document.documentElement.toggleAttribute('data-cart-safety-blocked', locked);
    document.documentElement.toggleAttribute('data-cart-safety-adding', adding && busy());
    document.querySelectorAll(checkoutSelector).forEach(el => {
      if (locked) {
        if (!locks.has(el)) locks.set(el, { disabled: el.disabled, aria: el.getAttribute('aria-disabled') });
        if ('disabled' in el && !el.disabled) el.disabled = true;
        if (el.getAttribute('aria-disabled') !== 'true') el.setAttribute('aria-disabled', 'true');
        el.setAttribute('data-cart-safety-locked', '');
      }
    });
    document.querySelectorAll(expressSelector).forEach(el => {
      if (locked) {
        if (!expressLocks.has(el)) expressLocks.set(el, el.hasAttribute('inert'));
        if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
        el.setAttribute('data-cart-safety-express-locked', '');
      }
    });
    // Line-number edits must not race an addition/removal that reorders lines.
    document.querySelectorAll('cart-drawer .cart-items__table-row[data-line], cart-items-component .cart-items__table-row[data-line]').forEach(el => {
      if (busy() && !editLocks.has(el)) {
        editLocks.set(el, el.hasAttribute('inert'));
        el.setAttribute('inert', '');
      }
    });
    for (const [el, wasInert] of editLocks) {
      if (!busy() && !wasInert) el.removeAttribute('inert');
      if (!busy() || !el.isConnected) editLocks.delete(el);
    }
    for (const [el, previous] of locks) {
      if (!locked) {
        if ('disabled' in el) el.disabled = previous.disabled || (lastCart?.item_count === 0 && !!el.closest('cart-drawer, cart-items-component'));
        if (previous.aria === null) el.removeAttribute('aria-disabled');
        else el.setAttribute('aria-disabled', previous.aria);
        el.removeAttribute('data-cart-safety-locked');
      }
      if (!locked || !el.isConnected) locks.delete(el);
    }
    for (const [el, wasInert] of expressLocks) {
      if (!locked) {
        if (!wasInert) el.removeAttribute('inert');
        el.removeAttribute('data-cart-safety-express-locked');
      }
      if (!locked || !el.isConnected) expressLocks.delete(el);
    }
    document.querySelectorAll('cart-drawer .drawer__inner, cart-items-component').forEach(statusPanel);
    persist();
  }

  function queueUI() {
    if (!uiQueued) { uiQueued = true; queueMicrotask(updateUI); }
  }

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    // Do not compete with the quick-add modal's focus trap. Its existing handler
    // closes the modal after the response; the final refresh opens the drawer.
    if (document.querySelector('quick-add-modal[open]')) return;
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('open')) {
      try { drawer.open(document.activeElement); } catch (_) { /* Retry after scripts finish loading. */ }
    }
    queueUI();
  }

  function begin(kind = 'change') {
    const operation = { kind, slow: false };
    pending.add(operation);
    revision++;
    dirty = true;
    verified = false;
    if (kind === 'add') {
      openRequested = true;
      adding = true;
      checkoutHoldUntil = Date.now() + 1000;
      clearTimeout(checkoutHoldTimer);
      checkoutHoldTimer = setTimeout(updateUI, 1000);
    }
    operation.timer = setTimeout(() => {
      if (!pending.has(operation)) return;
      operation.slow = true;
      updateUI();
    }, 10000);
    updateUI();
    if (kind === 'add') setTimeout(openDrawer, 0);
    return operation;
  }

  function finish(operation, failure = '') {
    if (!pending.delete(operation)) return;
    clearTimeout(operation.timer);
    clearTimeout(operation.abortTimer);
    if (failure) { error = failure; errorRevision = revision; }
    scheduleRefresh();
    updateUI();
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { if (!pending.size) reconcile(); }, 100);
  }

  function fail(text = 'We couldn’t confirm your last cart change. Review the items below before continuing.') {
    error = text;
    errorRevision = revision;
    verified = false;
    dirty = true;
    scheduleRefresh();
    updateUI();
  }

  async function readJSON(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await originalFetch.call(window, url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart verification failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  const itemIdentity = item => JSON.stringify([
    item.variant_id || item.id,
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    item.selling_plan_allocation?.selling_plan?.id || null,
  ]);
  const snapshot = cart => JSON.stringify([cart.token, cart.currency, cart.total_price,
    cart.items.map(item => [itemIdentity(item), item.quantity, item.final_line_price])]);

  function confirmAddition(data) {
    const items = data.items || [data];
    if (!items.length) throw new Error('Missing added item');
    for (const item of items) {
      if (!item.id || !Number.isInteger(item.quantity)) throw new Error('Invalid added item');
      const key = itemIdentity(item);
      additions.set(key, Math.max(additions.get(key) || 0, item.quantity));
    }
  }

  function sectionMatches(html, selector, cart) {
    if (typeof html !== 'string') return false;
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!section) return false;
    const rows = [...section.querySelectorAll('.cart-items__table-row[data-line]')];
    return rows.length === cart.items.length && rows.every((row, index) => {
      const input = row.querySelector('input[data-quantity-variant-id]');
      return Number(input?.dataset.quantityVariantId) === cart.items[index].variant_id &&
        Number(input?.value) === cart.items[index].quantity;
    });
  }

  async function reconcile() {
    if (pending.size || checking) return;
    checking = true;
    dirty = true;
    verified = false;
    updateUI();
    const version = revision;
    try {
      const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(el => ids.add(el.dataset.sectionId));
      let cart, sections;
      // Read on both sides of section rendering: an app or another tab may
      // change the session/cart while these requests are in flight.
      for (let attempt = 0; attempt < 2; attempt++) {
        const before = await readJSON(root() + 'cart.js');
        sections = await readJSON(location.pathname + '?sections=' + [...ids].map(encodeURIComponent).join(','));
        cart = await readJSON(root() + 'cart.js');
        if (version !== revision || pending.size) return;
        if (snapshot(before) === snapshot(cart)) break;
        if (attempt === 1) throw new Error('Cart changed during verification');
      }
      if (version !== revision || pending.size) return;
      if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('Invalid cart');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && !sectionMatches(sections['cart-drawer'], '#CartDrawer', cart)) throw new Error('Cart section does not match');
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(el => {
        if (!sectionMatches(sections[el.dataset.sectionId], 'cart-items-component', cart)) throw new Error('Cart page does not match');
      });
      const quantities = new Map();
      cart.items.forEach(item => quantities.set(itemIdentity(item), (quantities.get(itemIdentity(item)) || 0) + item.quantity));
      const missingAddition = [...additions].some(([key, quantity]) => (quantities.get(key) || 0) < quantity);
      if (missingAddition) error = uncertain;
      else if (errorRevision < 0 || (additions.size && errorRevision < version) || cart.item_count === 0) error = '';
      rendering = true;
      try {
        if (drawer) {
          const keepOpen = !document.querySelector('quick-add-modal[open]') && (openRequested || drawer.classList.contains('open'));
          drawer.renderContents({ sections }, { open: keepOpen });
          drawer.classList.toggle('is-empty', cart.item_count === 0);
        }
        document.querySelectorAll('cart-items-component').forEach(el => {
          const id = el.dataset.sectionId;
          if (!sections[id]) throw new Error('Missing cart page section');
          el.updateSections({ [id]: sections[id] });
        });
      } finally { rendering = false; }
      lastCart = cart;
      if (!missingAddition) additions.clear();
      adding = false;
      dirty = false;
      verified = true;
      openRequested = openRequested && !!document.querySelector('quick-add-modal[open]');
      document.dispatchEvent(new CustomEvent('cart:safety-synced', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { ...cart, _wwCartConfirmed: true } }));
    } catch (_) {
      error = 'We couldn’t check your cart. Please check it again before continuing.';
      dirty = false;
    } finally {
      checking = false;
      if (version !== revision || pending.size) { dirty = true; scheduleRefresh(); }
      updateUI();
    }
  }

  function classify(url, method, body) {
    let parsed;
    try { parsed = new URL(url, location.href); } catch (_) { return null; }
    if (parsed.origin !== location.origin || !/^(POST|PUT|PATCH|DELETE)$/i.test(method)) return null;
    const match = parsed.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    if (!match) return null;
    if (match[1] !== 'update') return match[1];
    // Analytics attributes and notes must not continuously lock checkout.
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return [...body.keys()].some(key => /^(updates(?:\[|$)|discount$)/.test(key)) ? 'update' : null;
    }
    if (typeof body === 'string') {
      try { const value = JSON.parse(body); return value && ('updates' in value || 'discount' in value) ? 'update' : null; }
      catch (_) { return /(?:^|&)(?:updates(?:%5B|\[|=)|discount=)/i.test(body) ? 'update' : null; }
    }
    return 'update'; // Request/stream bodies are conservatively tracked.
  }

  const uncertain = 'We couldn’t confirm your last cart change. Review the items below before continuing.';
  window.fetch = function(input, init) {
    const kind = classify(input instanceof Request ? input.url : input, init?.method || (input instanceof Request ? input.method : 'GET'), init?.body);
    if (!kind) return originalFetch.apply(this, arguments);
    const operation = begin(kind);
    operation.variantId = requestedVariant(init?.body);
    const controller = new AbortController();
    const callerSignal = init?.signal || (input instanceof Request ? input.signal : null);
    const abort = () => controller.abort();
    if (callerSignal?.aborted) abort();
    else callerSignal?.addEventListener('abort', abort, { once: true });
    operation.abortTimer = setTimeout(abort, 20000);
    let responsePromise;
    try { responsePromise = originalFetch.call(this, input, { ...init, signal: controller.signal }); }
    catch (err) { finish(operation, uncertain); throw err; }
    responsePromise.then(async response => {
      try {
        const data = await response.clone().json();
        const failure = !response.ok || data.status >= 400 || data.errors;
        if (!failure && kind === 'add') confirmAddition(data);
        finish(operation, failure ? failureMessage(data, operation) : '');
      } catch (_) { finish(operation, uncertain); }
    }, () => finish(operation, uncertain)).catch(() => finish(operation, uncertain)).finally(() => callerSignal?.removeEventListener('abort', abort));
    return responsePromise;
  };

  // Some cart apps use XMLHttpRequest instead of fetch.
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrRequests = new WeakMap();
  XMLHttpRequest.prototype.open = function(method, url) {
    xhrRequests.set(this, { method, url });
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function(body) {
    const request = xhrRequests.get(this);
    const kind = request && classify(request.url, request.method, body);
    if (!kind) return xhrSend.apply(this, arguments);
    const operation = begin(kind);
    operation.variantId = requestedVariant(body);
    operation.abortTimer = setTimeout(() => this.abort(), 20000);
    this.addEventListener('loadend', () => {
      try {
        const data = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
        const failure = this.status < 200 || this.status >= 300 || data.status >= 400 || data.errors;
        if (!failure && kind === 'add') confirmAddition(data);
        finish(operation, failure ? failureMessage(data, operation) : '');
      } catch (_) { finish(operation, uncertain); }
    }, { once: true });
    try { return xhrSend.apply(this, arguments); }
    catch (err) { finish(operation, uncertain); throw err; }
  };

  function stopCheckout(event) {
    if (!blocked()) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    openDrawer();
    return true;
  }
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest(checkoutSelector + ', ' + expressSelector)) stopCheckout(event);
    if (target?.closest('[data-cart-safety-check]')) {
      event.preventDefault();
      if (!pending.size) { dirty = true; reconcile(); }
    }
    if (target?.closest('[data-cart-safety-accept]') && !busy() && verified) {
      event.preventDefault();
      error = '';
      additions.clear();
      updateUI();
    }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, location.href).pathname;
    if (event.submitter?.name === 'checkout' || /\/checkouts?\b/.test(action) || form.id === 'CartDrawerCouponForm' || (/\/cart\/?$/.test(action) && !event.submitter)) {
      if (stopCheckout(event)) return;
    }
    // Delegated: survives every drawer replacement. Keep the existing coupon
    // behavior, but route it through the same checkout gate, including Enter.
    if (form.id === 'CartDrawerCouponForm') {
      event.preventDefault();
      event.stopImmediatePropagation();
      const code = form.querySelector('[name="discount_code"]')?.value.trim();
      if (code) location.assign(root() + 'checkout?discount=' + encodeURIComponent(code));
    }
  }, true);

  // Legacy app/theme code can call form.submit(), which skips submit events.
  const nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function() {
    const action = new URL(this.action, location.href).pathname;
    if (blocked() && (/\/checkouts?\b/.test(action) || /\/cart\/?$/.test(action))) {
      openDrawer();
      return;
    }
    return nativeSubmit.apply(this, arguments);
  };

  function requestRefresh() {
    revision++; dirty = true; verified = false; updateUI(); scheduleRefresh();
  }

  window.WWCartSafety = {
    begin, finish, fail,
    // For multi-request theme flows, also bound their preparatory reads and
    // discount calls. Mutations still pass through the shared observer above.
    fetchWithTimeout: async (input, init = {}) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await window.fetch(input, { ...init, signal: controller.signal });
        if (!response.ok) throw new Error('Cart request failed');
        await response.clone().arrayBuffer();
        return response;
      } finally { clearTimeout(timer); }
    },
    isBlocked: blocked,
    lineKey: line => lastCart?.items[Number(line) - 1]?.key,
    shouldDeferRender: () => {
      if (rendering) return false;
      // A late theme/app response must not overwrite an already verified cart.
      // All legacy render attempts go through a fresh, coordinated snapshot.
      if (!busy()) requestRefresh();
      return true;
    },
    refresh: requestRefresh,
    getState: () => ({ pending: pending.size, checking, dirty, error, verified, revision }),
  };

  function requestedVariant(body) {
    if (body instanceof FormData || body instanceof URLSearchParams) return body.get('id');
    try { const data = JSON.parse(body); return data.id || (data.items?.length === 1 ? data.items[0].id : null); } catch (_) { return null; }
  }

  function failureMessage(data, operation) {
    const description = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : uncertain;
    const limit = description.match(/only\s+(\d+)\s+(?:items?\s+)?(?:available|left)/i);
    if (limit) return `Only ${limit[1]} available`;
    if (data.status === 422 && /can't add more|cannot add more|already have|maximum quantity.*already/i.test(description)) {
      const sticky = document.querySelector('sticky-atc');
      if (operation?.variantId && sticky?.querySelector('input[name="id"]')?.value === String(operation.variantId)) {
        const button = sticky.querySelector('button[data-inventory-quantity]');
        const available = Number(button?.dataset.inventoryQuantity);
        if (button?.dataset.inventoryPolicy === 'deny' && available > 0) return `Only ${available} available`;
      }
    }
    return description;
  }

  try { if (sessionStorage.getItem(storageKey)) dirty = true; } catch (_) {}
  const start = () => {
    new MutationObserver(records => {
      if (records.some(record => record.type === 'attributes' || [...record.addedNodes].some(node => node.nodeType === 1 && !node.matches?.('[data-cart-safety-status]')))) queueUI();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'inert', 'open'] });
    updateUI();
    if (dirty) scheduleRefresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', event => {
    if (event.persisted) { revision++; dirty = true; verified = false; updateUI(); scheduleRefresh(); }
  });
})();


